/**
 * useCalendlyEvents Hook
 *
 * Loads the meetings booked through a location's connected Calendly so the
 * calendar can show them next to Chekkit events and reminders. Works for any
 * location; it does not need AI Employee. Calendly meetings are read-only.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import {
	CalendarEventType,
	CalendarViewType,
} from '@/components/event-calendar/types';
import { aiGet } from '@/components/ai-employee/hooks/use-ai-api';
import { getVisibleRange } from '@/utils/googleCalendarEvents';
import {
	CalendlyEventDTO,
	toCalendlyCalendarEvent,
} from '@/utils/calendlyEvents';

interface CalendlyEventsResponse {
	status: 'ready' | 'disconnected' | 'reconnect';
	name: string | null;
	events: CalendlyEventDTO[];
}

export type CalendlyStatus =
	'off' | 'loading' | 'error' | CalendlyEventsResponse['status'];

const REFRESH_ON_FOCUS_AFTER_MS = 60 * 1000;

export const useCalendlyEvents = ({
	currentDate,
	currentView,
}: {
	currentDate: Date;
	currentView: CalendarViewType;
}) => {
	const businessId = useSelector(
		(state: { auth?: { _id?: string } }) => state.auth?._id
	);
	const [status, setStatus] = useState<CalendlyStatus>(
		businessId ? 'loading' : 'off'
	);
	const [name, setName] = useState<string | null>(null);
	const [items, setItems] = useState<CalendlyEventDTO[]>([]);
	const latestRequest = useRef(0);
	const loadedAt = useRef(0);

	const { start, end } = getVisibleRange(currentDate, currentView);
	const startISO = start.toISOString();
	const endISO = end.toISOString();

	const load = useCallback(async () => {
		if (!businessId) {
			setStatus('off');
			return;
		}

		const request = ++latestRequest.current;
		loadedAt.current = Date.now();

		try {
			const query = new URLSearchParams({ start: startISO, end: endISO });
			const data = await aiGet<CalendlyEventsResponse>(
				`/api/calendar/calendly/${businessId}/events?${query}`
			);
			if (request !== latestRequest.current) return;

			setStatus(data.status);
			setName(data.name);
			setItems(data.events);
		} catch (error) {
			if (request !== latestRequest.current) return;
			// Keep what's already on screen; a failed refresh shouldn't blank it
			console.error('Failed to load Calendly events:', error);
			setStatus('error');
		}
	}, [businessId, startISO, endISO]);

	useEffect(() => {
		load();
	}, [load]);

	// Pick up meetings booked while the tab was in the background
	useEffect(() => {
		const handleFocus = () => {
			if (Date.now() - loadedAt.current > REFRESH_ON_FOCUS_AFTER_MS)
				load();
		};

		window.addEventListener('focus', handleFocus);
		return () => window.removeEventListener('focus', handleFocus);
	}, [load]);

	const events = useMemo<CalendarEventType[]>(
		() => items.map((item) => toCalendlyCalendarEvent(item, name)),
		[items, name]
	);

	return { status, name, events, reload: load };
};
